import { CustomerWaiver } from '@types';
import { PDFViewerModal, SearchBar } from '_components/atoms';
import AdminService from '_services/AdminService';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useState } from 'react';

interface SearchCustomerWaiversProps {
  onAttach?: (waiver: CustomerWaiver) => void;
  onUnattach?: () => void;
  attachedWaiverId?: number | string | null;
}

const SearchCustomerWaivers: React.FC<SearchCustomerWaiversProps> = ({
  onAttach,
  onUnattach,
  attachedWaiverId,
}) => {
  const [waivers, setWaivers] = useState<CustomerWaiver[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  const handleSearch = useCallback(async (searchTerm: string) => {
    console.log('handleSearch called with:', searchTerm);
    console.log('handleSearch searchTerm length:', searchTerm.length);
    console.log('handleSearch searchTerm trimmed:', searchTerm.trim());
    console.log(
      'handleSearch searchTerm trimmed length:',
      searchTerm.trim().length,
    );

    if (!searchTerm.trim()) {
      console.log('Search term is empty after trim, returning');
      setWaivers([]);
      setSearchPerformed(false);
      return;
    }

    try {
      setLoading(true);
      setSearchPerformed(true);
      console.log('Calling API with:', searchTerm.trim());
      const responseData = (await AdminService.searchCustomerWaivers(
        searchTerm.trim(),
      )) as unknown as CustomerWaiver[];

      if (responseData) {
        setWaivers(responseData);
      } else {
        setWaivers([]);
      }
    } catch (error) {
      console.error('Error searching waivers:', error);
      setWaivers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <SearchBar
        placeholder="Search by barcode or customer email..."
        onSearch={handleSearch}
        debounceMs={500}
      />

      {/* Waivers Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skater Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!searchPerformed ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Enter a barcode or customer email to search for waivers
                  </td>
                </tr>
              ) : waivers.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {loading ? 'Searching...' : 'No waivers found'}
                  </td>
                </tr>
              ) : (
                waivers.map(waiver => {
                  const isAttached =
                    attachedWaiverId === waiver.id ||
                    attachedWaiverId === waiver.pdfFilePath ||
                    attachedWaiverId ===
                      `${waiver.pdfFilePath}_${waiver.barcodeId}`;
                  return (
                    <tr
                      key={waiver.id}
                      className={`hover:bg-gray-50 ${isAttached ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {waiver.skaterName}
                            </div>
                            <div className="text-sm text-gray-500">
                              DOB:{' '}
                              {HelperMethods.formatDate(waiver.dateOfBirth) ||
                                '—'}
                            </div>
                            {typeof waiver.isAdult === 'boolean' && (
                              <div className="text-xs text-gray-400">
                                {waiver.isAdult ? 'Adult' : 'Minor'}
                              </div>
                            )}
                            {HelperMethods.formatWaiverSignedBy(
                              waiver.signedBy,
                            ) && (
                              <div className="text-xs text-gray-500">
                                Signed by:{' '}
                                {HelperMethods.formatWaiverSignedBy(
                                  waiver.signedBy,
                                )}
                              </div>
                            )}
                            <div className="text-xs text-gray-700">
                              Barcode:{' '}
                              <span className="font-semibold">
                                {waiver.barcodeId}
                              </span>
                            </div>
                            <div className="text-xs text-gray-400">
                              Submitted:{' '}
                              <span className="font-semibold">
                                {HelperMethods.formatDateTimeLocal(
                                  waiver.submittedAt,
                                )}
                              </span>
                            </div>
                          </div>
                          {isAttached && (
                            <div className="ml-3">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Attached
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {waiver.email}
                        </div>
                        <div className="text-sm text-gray-500">
                          {waiver.phoneNumber}
                        </div>
                        {waiver.emergencyContactPhone && (
                          <div className="text-xs text-gray-400">
                            Emergency: {waiver.emergencyContactPhone}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3 justify-center">
                          {waiver.pdfFilePath && (
                            <button
                              onClick={() =>
                                openPdfViewer(
                                  waiver.pdfFilePath!,
                                  `Waiver - ${waiver.skaterName}`,
                                )
                              }
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                            >
                              View Waiver
                            </button>
                          )}
                          {onAttach &&
                            (isAttached ? (
                              <button
                                onClick={() => onUnattach?.()}
                                className="text-red-600 hover:text-red-900 transition-colors"
                              >
                                Unattach
                              </button>
                            ) : (
                              <button
                                onClick={() => onAttach(waiver)}
                                className="text-green-600 hover:text-green-900 transition-colors"
                              >
                                Attach
                              </button>
                            ))}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Waivers Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {!searchPerformed ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            Enter a barcode or customer email to search for waivers
          </div>
        ) : waivers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {loading ? 'Searching...' : 'No waivers found'}
          </div>
        ) : (
          waivers.map(waiver => {
            const isAttached = attachedWaiverId === waiver.id;
            return (
              <div
                key={waiver.id}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${
                  isAttached ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {/* Skater Info */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {waiver.skaterName}
                    </h3>
                    {isAttached && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Attached
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>
                      DOB: {HelperMethods.formatDate(waiver.dateOfBirth)}
                    </div>
                    <div className="text-xs text-gray-400">
                      {waiver.isAdult ? 'Adult' : 'Minor'}
                    </div>
                    <div className="text-xs text-gray-700">
                      Barcode: {waiver.barcodeId}
                    </div>
                    <div>Email: {waiver.email}</div>
                    <div>Phone: {waiver.phoneNumber}</div>
                    {waiver.emergencyContactPhone && (
                      <div className="text-xs text-gray-400">
                        Emergency: {waiver.emergencyContactPhone}
                      </div>
                    )}
                    <div className="text-xs text-gray-400">
                      Submitted:{' '}
                      {HelperMethods.formatDateTimeLocal(waiver.submittedAt)}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {waiver.pdfFilePath && (
                    <button
                      onClick={() =>
                        openPdfViewer(
                          waiver.pdfFilePath!,
                          `Waiver - ${waiver.skaterName}`,
                        )
                      }
                      className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                    >
                      View Waiver
                    </button>
                  )}
                  {onAttach &&
                    (isAttached ? (
                      <button
                        onClick={() => onUnattach?.()}
                        className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors"
                      >
                        Unattach
                      </button>
                    ) : (
                      <button
                        onClick={() => onAttach(waiver)}
                        className="inline-flex items-center px-3 py-2 bg-green-100 text-green-700 text-sm rounded-md hover:bg-green-200 transition-colors"
                      >
                        Attach
                      </button>
                    ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default SearchCustomerWaivers;
